import Routes from "@/routes/Routes";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useEffect, useMemo } from "react";
import CacheBuster from "react-cache-buster";
import { RouterProvider } from "react-router-dom";
import pkg from "../package.json";
import "./locale/i18n";
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import { Toast } from "./components/kit";
import LanguageProvider from "./provider/LanguageProvider";
import { DarkModeProvider } from "./provider/DarkModeProvider";
import { ConfirmationContextProvider } from "@/context/ConfirmationContext.tsx";
import * as Sentry from "@sentry/react";
import { AuthProvider } from "@/contexts/AuthContext";

const isProduction = import.meta.env.MODE === "production";

declare global {
  interface Window {
    pendo?: {
      initialize: (config: {
        visitor: {
          id: string;
          email?: string;
          firstName?: string;
          lastName?: string;
        };
      }) => void;
      track: (eventName: string, metadata?: Record<string, any>) => void;
      pageLoad: () => void;
    };
  }
}

// Convert any value into a safe primitive for Sentry tag usage
const toTagPrimitive = (value: unknown): string => {
  if (value === null || value === undefined) return "unknown";
  const type = typeof value;
  if (type === "string" || type === "number" || type === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "unserializable";
  }
};

const initializePendoScript = () => {
  if (typeof window === "undefined" || window.pendo) return;

  const script = document.createElement("script");
  script.innerHTML = `
    (function(apiKey){
      (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
      v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
      o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
      y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
      z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
    })('d1029550-2115-4e28-8724-1294908d34a7');
  `;
  document.head.appendChild(script);
};

function App() {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 4,
          },
        },
        mutationCache: new MutationCache({
          onError: (error: any, variables, _context, mutation) => {
            const { meta } = mutation;
            let mutationKeyFirst: unknown = mutation.options.mutationKey;
            if (Array.isArray(mutation.options.mutationKey)) {
              mutationKeyFirst = mutation.options.mutationKey[0];
            }
            const mutationKeyTag = toTagPrimitive(mutationKeyFirst);
            Sentry.captureException(error, {
              tags: {
                type: "mutation_error",
                mutationKey: mutationKeyTag,
              },
              extra: {
                variables,
                mutationKey: mutation.options.mutationKey,
                meta,
              },
            });
            const errorMessage =
              error?.response?.data?.error?.message ||
              error?.response?.data?.message ||
              error?.response?.data?.error;
            let exactError: string;
            if (Array.isArray(errorMessage)) {
              exactError = `${errorMessage?.[0].replaceAll("_", " ")}`;
            } else {
              exactError = `${errorMessage?.replaceAll("_", " ")}`;
            }
            if (meta?.showError === false) return;
            Toast({
              description: exactError,
              status: "error",
              title: "Error",
            });
          },
        }),
        queryCache: new QueryCache({
          onError: (error, query) => {
            const { meta } = query;
            let queryKeyFirst: unknown = query.queryKey;
            if (Array.isArray(query.queryKey)) {
              queryKeyFirst = query.queryKey[0];
            }
            const queryKeyTag = toTagPrimitive(queryKeyFirst);

            // Capture query errors in Sentry
            Sentry.captureException(error, {
              tags: {
                type: "query_error",
                queryKey: queryKeyTag,
              },
              extra: {
                queryKey: query.queryKey,
                meta,
                state: query.state,
              },
            });

            if (meta) {
              console.log(meta.message);
            }
          },
        }),
      }),
    [],
  );

  useEffect(() => {
    polyfillCountryFlagEmojis();

    initializePendoScript();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {!isProduction && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
      <LanguageProvider>
        <DarkModeProvider>
          <CacheBuster
            currentVersion={pkg.version}
            isEnabled={isProduction} //If false, the library is disabled.
            isVerboseMode={!isProduction} //If true, the library writes verbose logs to console.
            loadingComponent={<p></p>} //If not pass, nothing appears at the time of new version check.
          >
            <AuthProvider>
              <ConfirmationContextProvider>
                <RouterProvider router={Routes} />
              </ConfirmationContextProvider>
            </AuthProvider>
          </CacheBuster>
        </DarkModeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
